/**
 * FTR-COCKPIT: framed first-person cockpit pass (client-only).
 * Two-pass composite per wave0-engineering-reference.md §1:
 *   world pass (composer) → clearDepth → cockpit render.
 * AAA-21: NO emissive / glow / bloom on any cockpit surface.
 * Functions ≤50 lines; sim-core untouched.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { CameraMode } from './cameras.js';
import type { CameraRig } from './cameras.js';
import type {
  BrowGeom, CockpitConfig, CockpitGeometry, CockpitMaterials,
  ConsoleGeom, DashGeom, InstrumentBay, InstrumentBays, PillarGeom, StackGeom,
} from './cockpitConfig.js';
import { DEFAULT_COCKPIT_GEOMETRY, getCockpitConfig } from './cockpitConfig.js';
import { CockpitInstruments } from './cockpitInstruments.js';
import type { HudFrame } from '../hud/hud.js';

// Wave 2b free win: the cockpit scene now receives the REAL skybox environment map
// at a low intensity, so the matte charcoal reads as lit painted metal instead of a
// flat void-black slab. AAA-21 preserved — this is a dim specular sheen, NOT emissive.
const COCKPIT_ENV_INTENSITY = 0.18;

// Matte charcoal — AAA-21: no emissive, roughness high, metalness near-zero. envMap
// intensity is now a low non-zero so the shared env map lights the dash subtly.
const buildMaterial = (hex: number, roughness: number, metalness: number): THREE.MeshStandardMaterial =>
  new THREE.MeshStandardMaterial({ color: hex, roughness, metalness, envMapIntensity: COCKPIT_ENV_INTENSITY });

// The canopy frame gets a subtle clearcoat lacquer lobe (curved glass read) — a
// specular second layer, still zero-emissive. Clearcoat is applied ONLY here.
const buildCanopyMaterial = (hex: number, roughness: number, metalness: number): THREE.MeshPhysicalMaterial =>
  new THREE.MeshPhysicalMaterial({
    color: hex, roughness, metalness,
    envMapIntensity: COCKPIT_ENV_INTENSITY,
    clearcoat: 0.6, clearcoatRoughness: 0.12,
  });

const buildMaterials = (m: CockpitMaterials): [THREE.MeshStandardMaterial, THREE.MeshStandardMaterial] => [
  buildCanopyMaterial(m.canopyHex, m.canopyRoughness ?? m.roughness, m.canopyMetalness ?? m.metalness),
  buildMaterial(m.dashHex, m.dashRoughness ?? m.roughness, m.dashMetalness ?? m.metalness),
];

interface Transform {
  x?: number;
  y?: number;
  z?: number;
  rx?: number;
  ry?: number;
  rz?: number;
}

const transform = (geometry: THREE.BufferGeometry, t: Transform): THREE.BufferGeometry => {
  if (t.rx) geometry.rotateX(t.rx);
  if (t.ry) geometry.rotateY(t.ry);
  if (t.rz) geometry.rotateZ(t.rz);
  return geometry.applyMatrix4(new THREE.Matrix4().makeTranslation(t.x ?? 0, t.y ?? 0, t.z ?? 0));
};

const buildBox = (width: number, height: number, depth: number, t: Transform): THREE.BufferGeometry =>
  transform(new THREE.BoxGeometry(width, height, depth), t);

/** Two raked A-pillars (mirrored ±x by spread), tops tilted back away from the pilot. */
function buildPillars(p: PillarGeom): THREE.BufferGeometry {
  const make = (sign: number): THREE.BufferGeometry =>
    buildBox(p.width, p.height, p.depth, { x: sign * p.spread, y: p.y, z: p.z, rx: -p.rake });
  return mergeGeometries([make(-1), make(1)]);
}

/** Brow corner block + short bridge so the A-pillars clearly carry into the brow. */
function buildBrowJoint(b: BrowGeom, p: PillarGeom, sign: number): THREE.BufferGeometry {
  const bridgeWidth = Math.max(0.08, b.width / 2 - p.spread + p.width * 0.55);
  const cornerX = sign * (b.width / 2 - b.cornerWidth * 0.55);
  const bridgeX = sign * (p.spread + bridgeWidth * 0.5 - p.width * 0.08);
  return mergeGeometries([
    buildBox(b.cornerWidth, b.cornerHeight, b.cornerDepth, { x: cornerX, y: b.y - b.cornerHeight * 0.18, z: b.z, rx: -p.rake * 0.55 }),
    buildBox(bridgeWidth, b.cornerHeight * 0.72, b.cornerDepth * 0.72, {
      x: bridgeX, y: b.y - b.cornerHeight * 0.08, z: (b.z + p.z) * 0.5 + 0.02,
    }),
  ]);
}

/** Overhead brow beam capping the canopy across the pillar tops. */
function buildBrow(b: BrowGeom, p: PillarGeom): THREE.BufferGeometry {
  return mergeGeometries([
    buildBox(b.width, b.height, b.depth, { x: 0, y: b.y, z: b.z }),
    buildBrowJoint(b, p, -1),
    buildBrowJoint(b, p, 1),
  ]);
}

/** One stepped side console pod with a beveled face to keep the lower corners off flat slabs. */
function buildConsolePod(c: ConsoleGeom, sign: number): THREE.BufferGeometry {
  const x = sign * c.x;
  const ry = -sign * c.toe;
  return mergeGeometries([
    buildBox(c.width, c.height, c.depth, { x, y: c.y, z: c.z, ry }),
    buildBox(c.width * 0.9, c.capHeight, c.depth * 0.72, { x: x - sign * c.width * 0.02, y: c.y + c.height * 0.33, z: c.z - c.depth * 0.02, ry }),
    buildBox(c.width * 0.78, c.faceHeight, c.faceDepth, {
      x: x - sign * c.width * 0.08, y: c.y + c.height * 0.14, z: c.z + c.depth * 0.24, rx: -0.38, ry,
    }),
    buildBox(c.width * 0.64, c.faceHeight * 0.72, c.faceDepth * 0.58, {
      x: x - sign * c.width * 0.1, y: c.y - c.height * 0.03, z: c.z + c.depth * 0.1, rx: -0.26, ry,
    }),
  ]);
}

/** Mirrored ±x side consoles, toed-in toward the centerline — frames the lower corners. */
function buildConsoles(c: ConsoleGeom): THREE.BufferGeometry {
  return mergeGeometries([buildConsolePod(c, -1), buildConsolePod(c, 1)]);
}

/** Symmetric stepped shoulder panels that tuck into the dash hood. */
function buildDashPanels(d: DashGeom, sign: number): THREE.BufferGeometry[] {
  const outerX = sign * (d.hoodWidth * 0.5 + d.panelInset + d.panelWidth * 0.5);
  const innerX = sign * (d.hoodWidth * 0.5 + d.panelInset * 0.5 + d.panelWidth * 0.34);
  const y = d.y + d.height * 0.66;
  const z = d.z + d.depth * 0.1;
  return [
    buildBox(d.panelWidth, d.panelHeight, d.panelDepth, { x: outerX, y, z }),
    buildBox(d.panelWidth * 0.68, d.panelHeight * 0.78, d.panelDepth * 0.72, {
      x: innerX, y: y + d.panelHeight * 0.56, z: z + d.panelDepth * 0.08,
    }),
  ];
}

/** Layered dash slab with a raised cowl, stepped shoulders, and an angled front lip. */
function buildDashBody(d: DashGeom): THREE.BufferGeometry {
  return mergeGeometries([
    buildBox(d.width, d.height, d.depth, { x: 0, y: d.y, z: d.z }),
    buildBox(d.width * 0.96, d.height * 0.48, d.lipDepth, {
      x: 0, y: d.y + d.height * 0.34, z: d.z + d.depth / 2 - d.lipDepth * 0.28, rx: d.lip,
    }),
    buildBox(d.hoodWidth, d.hoodHeight, d.hoodDepth, {
      x: 0, y: d.y + d.height * 0.74 + d.hoodHeight * 0.5, z: d.z + d.depth * 0.1,
    }),
    ...buildDashPanels(d, -1),
    ...buildDashPanels(d, 1),
  ]);
}

/** Raised center stack rising between the consoles below the sightline — trimmed
 *  and recessed so its front face sits BEHIND the centre velocity bay. */
function buildCenterStack(s: StackGeom): THREE.BufferGeometry {
  return mergeGeometries([
    buildBox(s.width, s.height, s.depth, { x: 0, y: s.y, z: s.z }),
    buildBox(s.width * 0.86, s.height * 0.24, s.depth * 0.7, {
      x: 0, y: s.y + s.height * 0.28, z: s.z + s.depth * 0.06,
    }),
  ]);
}

/** The thin raised bezel lip framing a bay's recessed opening: four chamfer-thin
 *  bars whose front face sits at local z=0 (the FRONTMOST geometry at this spot),
 *  giving the double-bezel catch-light edge. Built in the bay's local frame. */
function bezelFrame(b: InstrumentBay): THREE.BufferGeometry[] {
  const t = b.bezelThickness;
  const d = b.bezelHeight;
  const z = -d / 2; // front face flush at local z=0, lip standing depth d back
  const innerH = b.height - 2 * t;
  return [
    buildBox(b.width, t, d, { x: 0, y: b.height / 2 - t / 2, z }),
    buildBox(b.width, t, d, { x: 0, y: -b.height / 2 + t / 2, z }),
    buildBox(t, innerH, d, { x: -b.width / 2 + t / 2, y: 0, z }),
    buildBox(t, innerH, d, { x: b.width / 2 - t / 2, y: 0, z }),
  ];
}

/** One recessed instrument bay: a flat inset back-plate set back by pocketDepth +
 *  the raised bezel lip framing it. Built local, then reclined (pitch), toed (yaw)
 *  and seated — matching cockpitInstruments' baySeat() so the screen sits flush. */
function buildInstrumentBay(b: InstrumentBay): THREE.BufferGeometry {
  const plate = buildBox(b.width, b.height, 0.012, { x: 0, y: 0, z: -b.pocketDepth });
  const local = mergeGeometries([plate, ...bezelFrame(b)]);
  return transform(local, { x: b.x, y: b.y, z: b.z, rx: b.pitch, ry: b.yaw });
}

/** All three recessed bays (left pips / centre velocity / right status). */
function buildInstrumentBays(bays: InstrumentBays): THREE.BufferGeometry {
  return mergeGeometries([
    buildInstrumentBay(bays.left),
    buildInstrumentBay(bays.centre),
    buildInstrumentBay(bays.right),
  ]);
}

/** Canopy-material geometry (~1 draw call): pillars + brow + consoles + stack. */
function buildCanopyGeometry(g: CockpitGeometry): THREE.BufferGeometry {
  return mergeGeometries([
    buildPillars(g.pillar), buildBrow(g.brow, g.pillar),
    buildConsoles(g.consoles), buildCenterStack(g.centerStack),
  ]);
}

/** Dash-material geometry (~1 draw call): dash body + the three recessed bays. */
function buildDashGeometry(g: CockpitGeometry): THREE.BufferGeometry {
  return mergeGeometries([buildDashBody(g.dash), buildInstrumentBays(g.bays)]);
}

export class CockpitView {
  private readonly scene: THREE.Scene;
  private readonly cam: THREE.PerspectiveCamera;
  private readonly cabin: THREE.Group;
  private readonly config: CockpitConfig;
  // Each cabin mesh paired with its config-built material, kept so the lab can
  // swap to a clay override and restore the originals (game never touches these).
  private readonly cabinParts: { mesh: THREE.Mesh; material: THREE.MeshStandardMaterial }[];
  private labMaterial: THREE.MeshStandardMaterial | null = null;
  // Config lights live in a named group so the lab can toggle them off without
  // mutating geometry; the game default keeps this group visible (look unchanged).
  private readonly configLightGroup: THREE.Group;
  private labLightGroup: THREE.Group | null = null;
  // Part 2: diegetic core-instrument screens painted on the dash/consoles. Parented
  // in the cabin so they head-lock and render in the existing cockpit pass.
  private readonly instruments: CockpitInstruments;
  private visible = false;

  constructor(shipId = 'default') {
    this.config = getCockpitConfig(shipId);
    this.scene = new THREE.Scene();
    // near 0.01 far 5: isolates cockpit depth from the 10^12 world (no shared z-fight).
    this.cam = new THREE.PerspectiveCamera(this.config.fov, 1, 0.01, 5);
    const [canopyMat, dashMat] = buildMaterials(this.config.materials);
    const geo = this.config.geometry ?? DEFAULT_COCKPIT_GEOMETRY;
    this.cabin = new THREE.Group();
    this.cabinParts = [
      { mesh: new THREE.Mesh(buildCanopyGeometry(geo), canopyMat), material: canopyMat },
      { mesh: new THREE.Mesh(buildDashGeometry(geo), dashMat), material: dashMat },
    ];
    for (const part of this.cabinParts) this.cabin.add(part.mesh);
    this.instruments = new CockpitInstruments(geo);
    this.cabin.add(this.instruments.getGroup());
    this.cabin.traverse((node) => { node.frustumCulled = false; });
    this.scene.add(this.cabin);
    this.configLightGroup = new THREE.Group();
    this.scene.add(this.configLightGroup);
    this.addLights();
  }

  /**
   * Build the configured lights into configLightGroup. Directional and point
   * lights use their pos; ambient ignores it. The faint cyan point lamp seats
   * inside the cabin so consoles read as lit from within. AAA-21: intensities
   * stay low (Σ≲0.85) so the interior reads darker than the void, no emissive.
   */
  private addLights(): void {
    for (const light of this.config.lights) {
      const [x, y, z] = light.pos ?? [0, 0, 0];
      if (light.kind === 'directional') {
        const fill = new THREE.DirectionalLight(light.colorHex, light.intensity);
        fill.position.set(x, y, z);
        this.configLightGroup.add(fill);
      } else if (light.kind === 'point') {
        const lamp = new THREE.PointLight(light.colorHex, light.intensity, light.distance ?? 0, light.decay ?? 2);
        lamp.position.set(x, y, z);
        this.configLightGroup.add(lamp);
      } else {
        this.configLightGroup.add(new THREE.AmbientLight(light.colorHex, light.intensity));
      }
    }
  }

  /** The cockpit scene (cabin + lights). Lets the cockpit lab render it WYSIWYG. */
  getScene(): THREE.Scene {
    return this.scene;
  }

  /** The cockpit camera (fov/eyeOffset). The lab tunes fov + position on this. */
  getCamera(): THREE.PerspectiveCamera {
    return this.cam;
  }

  /** The active per-ship cockpit config (read-only) — the lab reports it in #readout. */
  getConfig(): CockpitConfig {
    return this.config;
  }

  /**
   * Lab-only lighting hook. null = use the config lights (the true in-game look).
   * Non-null = hide config lights and light with the supplied lab 'studio' rig.
   * The game never calls this, so the in-game path is unchanged (config visible).
   */
  setLightOverride(lights: THREE.Light[] | null): void {
    if (this.labLightGroup) {
      this.scene.remove(this.labLightGroup);
      this.labLightGroup = null;
    }
    this.configLightGroup.visible = lights === null;
    if (!lights) return;
    const group = new THREE.Group();
    for (const light of lights) group.add(light);
    this.scene.add(group);
    this.labLightGroup = group;
  }

  /**
   * Lab-only material hook. null = restore the config charcoal materials (the
   * true in-game look). Non-null = swap BOTH cabin meshes to one shared matte
   * MeshStandardMaterial so near-black geometry reads as legible mid-grey clay.
   * AAA-21 preserved: no emissive, envMapIntensity 0. The game never calls this.
   */
  setMaterialOverride(hex: number | null): void {
    if (hex === null) {
      this.labMaterial?.dispose();
      this.labMaterial = null;
      for (const part of this.cabinParts) part.mesh.material = part.material;
      return;
    }
    this.labMaterial?.dispose();
    this.labMaterial = new THREE.MeshStandardMaterial({
      color: hex, roughness: 0.85, metalness: 0.0, envMapIntensity: 0,
    });
    for (const part of this.cabinParts) part.mesh.material = this.labMaterial;
  }

  /**
   * Two-pass cockpit composite: clears depth then overlays canopy on the world.
   * Called AFTER composer.render() so the bloom/post pass is already on canvas.
   * When mode !== 'cockpit' this is a no-op (chase pays nothing).
   */
  render(
    renderer: THREE.WebGLRenderer,
    worldCam: THREE.PerspectiveCamera,
    rig: CameraRig,
    environment: THREE.Texture | null = null,
  ): void {
    if (!this.visible || rig.mode !== 'cockpit') return;
    // Mirror the live world environment (the PMREM skybox IBL) so the cockpit tracks
    // the same lighting the hull sees — cheap ref assignment, follows the async
    // gradient→skybox env swap for free. Lab renders bypass this (null default).
    if (environment !== null) this.scene.environment = environment;
    this.cam.fov = worldCam.fov;
    this.cam.aspect = worldCam.aspect;
    this.cam.updateProjectionMatrix();
    this.cam.quaternion.copy(rig.cockpitFrameDelta);
    const [ex, ey, ez] = this.config.eyeOffset;
    this.cam.position.set(ex, ey, ez);
    renderer.render(this.scene, this.cam);
  }

  /** Part 2: feed the live HudFrame to the diegetic dash/console screens. */
  updateInstruments(frame: HudFrame): void {
    this.instruments.update(frame);
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.cabin.visible = visible;
    // Tie instrument visibility to cockpit mode exactly like the cabin (hidden in chase).
    this.instruments.setVisible(visible);
  }

  /** Sync mesh visibility with camera mode (zero GPU cost when in chase). */
  setMode(mode: CameraMode): void {
    this.setVisible(mode === 'cockpit');
  }
}
